import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, Chip, Grid, Card,
  CardContent, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, LinearProgress, Alert, IconButton, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Checkbox, FormControlLabel, Switch,
} from '@mui/material';
import {
  Article, Add, Edit, Delete, Close, Download, CheckCircle,
  Send, Autorenew, Warning,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

async function apiFetchBlob(path: string) {
  const r = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}${path}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  });
  if (!r.ok) throw new Error('Download failed');
  const blob = await r.blob();
  const cd = r.headers.get('content-disposition') ?? '';
  const m = cd.match(/filename="([^"]+)"/);
  return { blob, filename: m?.[1] ?? 'export.csv' };
}

const FORM_TYPES: Record<string, { label: string; desc: string; color: string }> = {
  IR56B: { label: 'IR56B', desc: 'Annual Return (all employees)', color: '#1565c0' },
  IR56E: { label: 'IR56E', desc: 'New Employment Notice',          color: '#2e7d32' },
  IR56F: { label: 'IR56F', desc: 'Cessation of Employment',        color: '#e65100' },
  IR56G: { label: 'IR56G', desc: 'Notice of Departure from HK',    color: '#6a1b9a' },
};

const STATUS_COLORS: Record<string, any> = { draft: 'default', ready: 'warning', submitted: 'success' };

function fmt(n: number | string | undefined) {
  return Number(n ?? 0).toLocaleString('en-HK', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

interface Filing {
  id: string; employeeId: string; formType: string; yearOfAssessment: string;
  periodFrom: string; periodTo: string; basicSalary: number; bonus: number;
  commission: number; allowances: number; backpay: number; otherIncome: number;
  totalRemuneration: number; housingBenefit: number; otherBenefits: number;
  mpfEmployeeContrib: number; mpfEmployerContrib: number; position?: string;
  hkidOrPassport?: string; isBoardDirector: boolean; commencementDate?: string;
  cessationDate?: string; departureDate?: string; status: string;
  submittedAt?: string; irdReference?: string; notes?: string;
  employee?: { id: string; firstName: string; lastName: string; department?: { name: string } };
}

interface Stats { total: number; draft: number; ready: number; submitted: number; totalRemuneration: number; totalMpfEmployer: number; }

function StatCard({ label, value, color, icon }: any) {
  return (
    <Card sx={{ textAlign: 'center', p: 0.5 }}>
      <CardContent sx={{ pb: '12px !important' }}>
        <Box sx={{ color, mb: 0.5 }}>{icon}</Box>
        <Typography variant="h5" fontWeight={700} color={color}>{value}</Typography>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
      </CardContent>
    </Card>
  );
}

export default function IR56BPage() {
  const { user } = useAuth();

  // Year selector
  const [years, setYears] = useState<string[]>([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedFormType, setSelectedFormType] = useState('IR56B');
  const [statusFilter, setStatusFilter] = useState('');

  const [filings, setFilings] = useState<Filing[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(0);

  // Selection for bulk ops
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Dialogs
  const [generateDialog, setGenerateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState<Partial<Filing> | null>(null);
  const [submitDialog, setSubmitDialog] = useState<{ ids: string[]; ref: string } | null>(null);

  const loadYears = async () => {
    const y = await apiFetch('/api/ir56b/years');
    setYears(y);
    if (!selectedYear && y.length > 0) setSelectedYear(y[0]);
  };

  const load = useCallback(async () => {
    if (!selectedYear) return;
    setLoading(true); setError('');
    try {
      const [f, s] = await Promise.all([
        apiFetch(`/api/ir56b?year=${selectedYear}&formType=${selectedFormType}${statusFilter ? `&status=${statusFilter}` : ''}`),
        apiFetch(`/api/ir56b/stats?year=${selectedYear}`),
      ]);
      setFilings(f); setStats(s); setSelected(new Set());
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  }, [selectedYear, selectedFormType, statusFilter]);

  useEffect(() => { loadYears(); }, []);
  useEffect(() => { if (selectedYear) load(); }, [load, selectedYear]);

  const handleGenerate = async () => {
    try {
      const r = await apiFetch('/api/ir56b/bulk-generate', { method: 'POST', body: JSON.stringify({ yearOfAssessment: selectedYear }) });
      setGenerateDialog(false);
      load();
      setError('');
      // Show result
      alert(`Generated ${r.created} new filings (${r.skipped} already existed).`);
    } catch (e: any) { setError(e.message); }
  };

  const handleExport = async () => {
    try {
      const { blob, filename } = await apiFetchBlob(`/api/ir56b/export?year=${selectedYear}&formType=${selectedFormType}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) { setError(e.message); }
  };

  const handleBulkReady = async () => {
    if (selected.size === 0) return;
    try { await apiFetch('/api/ir56b/bulk-ready', { method: 'POST', body: JSON.stringify({ ids: Array.from(selected) }) }); load(); }
    catch (e: any) { setError(e.message); }
  };

  const handleBulkSubmit = async () => {
    if (!submitDialog) return;
    try {
      await apiFetch('/api/ir56b/bulk-submitted', { method: 'POST', body: JSON.stringify({ ids: submitDialog.ids, irdReference: submitDialog.ref }) });
      setSubmitDialog(null); load();
    } catch (e: any) { setError(e.message); }
  };

  const saveEdit = async () => {
    if (!editDialog) return;
    try {
      editDialog.id
        ? await apiFetch(`/api/ir56b/${editDialog.id}`, { method: 'PATCH', body: JSON.stringify(editDialog) })
        : await apiFetch('/api/ir56b', { method: 'POST', body: JSON.stringify(editDialog) });
      setEditDialog(null); load();
    } catch (e: any) { setError(e.message); }
  };

  const deleteFiling = async (id: string) => {
    if (!window.confirm('Delete this filing?')) return;
    try { await apiFetch(`/api/ir56b/${id}`, { method: 'DELETE' }); load(); }
    catch (e: any) { setError(e.message); }
  };

  const toggleAll = (checked: boolean) => {
    if (checked) setSelected(new Set(filings.map(f => f.id)));
    else setSelected(new Set());
  };

  const toggleOne = (id: string) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };

  const draftCount = filings.filter(f => f.status === 'draft').length;
  const readyCount = filings.filter(f => f.status === 'ready').length;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Article sx={{ fontSize: 32, color: '#c62828' }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>IR56B — HK Tax Filing</Typography>
            <Typography variant="body2" color="text.secondary">
              Annual Employer's Return (IRD) · IR56B / IR56E / IR56F / IR56G
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<Download />} onClick={handleExport} disabled={!selectedYear}>
            Export CSV
          </Button>
          <Button variant="contained" sx={{ bgcolor: '#c62828' }} startIcon={<Autorenew />}
            onClick={() => setGenerateDialog(true)} disabled={!selectedYear}>
            Generate IR56B
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Controls */}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField select label="Year of Assessment" size="small" sx={{ width: 160 }}
          value={selectedYear} onChange={e => setSelectedYear(e.target.value)}>
          {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
        </TextField>
        <TextField select label="Form Type" size="small" sx={{ width: 140 }}
          value={selectedFormType} onChange={e => setSelectedFormType(e.target.value)}>
          {Object.entries(FORM_TYPES).map(([k, v]) => (
            <MenuItem key={k} value={k}>{k} — {v.desc}</MenuItem>
          ))}
        </TextField>
        <TextField select label="Status" size="small" sx={{ width: 120 }}
          value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="draft">Draft</MenuItem>
          <MenuItem value="ready">Ready</MenuItem>
          <MenuItem value="submitted">Submitted</MenuItem>
        </TextField>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
          {filings.length} record{filings.length !== 1 ? 's' : ''}
        </Typography>
      </Paper>

      {/* Stats */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 6, sm: 2 }}>
            <StatCard label="Total Filings" value={stats.total} icon={<Article />} color="#1565c0" />
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <StatCard label="Draft" value={stats.draft} icon={<Article />} color="#757575" />
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <StatCard label="Ready" value={stats.ready} icon={<CheckCircle />} color="#f57c00" />
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <StatCard label="Submitted" value={stats.submitted} icon={<Send />} color="#2e7d32" />
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <StatCard label="Total Remuneration" value={`$${fmt(stats.totalRemuneration)}`} icon={<Article />} color="#4527a0" />
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <StatCard label="Total MPF (Employer)" value={`$${fmt(stats.totalMpfEmployer)}`} icon={<Article />} color="#c62828" />
          </Grid>
        </Grid>
      )}

      {/* Bulk actions */}
      {selected.size > 0 && (
        <Paper sx={{ p: 1.5, mb: 2, display: 'flex', gap: 1, alignItems: 'center', bgcolor: '#e8f5e9' }}>
          <Typography variant="body2" fontWeight={600}>{selected.size} selected</Typography>
          <Button size="small" variant="outlined" color="warning" onClick={handleBulkReady}>
            Mark Ready
          </Button>
          <Button size="small" variant="contained" color="success" startIcon={<Send />}
            onClick={() => setSubmitDialog({ ids: Array.from(selected), ref: '' })}>
            Mark Submitted
          </Button>
          <Button size="small" onClick={() => setSelected(new Set())}>Clear</Button>
        </Paper>
      )}

      {loading && <LinearProgress sx={{ mb: 1 }} />}

      {/* Filings table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#fafafa' }}>
              <TableCell padding="checkbox">
                <Checkbox indeterminate={selected.size > 0 && selected.size < filings.length}
                  checked={filings.length > 0 && selected.size === filings.length}
                  onChange={e => toggleAll(e.target.checked)} />
              </TableCell>
              <TableCell><b>Employee</b></TableCell>
              <TableCell><b>Dept</b></TableCell>
              <TableCell><b>HKID</b></TableCell>
              <TableCell><b>Position</b></TableCell>
              <TableCell align="right"><b>Basic Salary</b></TableCell>
              <TableCell align="right"><b>Bonus</b></TableCell>
              <TableCell align="right"><b>Total Rem.</b></TableCell>
              <TableCell align="right"><b>MPF (Ee)</b></TableCell>
              <TableCell align="right"><b>MPF (Er)</b></TableCell>
              <TableCell><b>Status</b></TableCell>
              <TableCell><b>IRD Ref</b></TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filings.map(f => (
              <TableRow key={f.id} hover selected={selected.has(f.id)}>
                <TableCell padding="checkbox">
                  <Checkbox checked={selected.has(f.id)} onChange={() => toggleOne(f.id)} />
                </TableCell>
                <TableCell>
                  <Typography fontWeight={700} variant="body2">
                    {f.employee?.lastName} {f.employee?.firstName}
                  </Typography>
                </TableCell>
                <TableCell><Typography variant="caption">{f.employee?.department?.name ?? '—'}</Typography></TableCell>
                <TableCell><Typography variant="caption">{f.hkidOrPassport ?? '—'}</Typography></TableCell>
                <TableCell><Typography variant="caption">{f.position ?? '—'}</Typography></TableCell>
                <TableCell align="right"><Typography variant="body2">${fmt(f.basicSalary)}</Typography></TableCell>
                <TableCell align="right"><Typography variant="body2">${fmt(f.bonus)}</Typography></TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={700}>${fmt(f.totalRemuneration)}</Typography>
                </TableCell>
                <TableCell align="right"><Typography variant="caption">${fmt(f.mpfEmployeeContrib)}</Typography></TableCell>
                <TableCell align="right"><Typography variant="caption">${fmt(f.mpfEmployerContrib)}</Typography></TableCell>
                <TableCell>
                  <Chip label={f.status} size="small" color={STATUS_COLORS[f.status] ?? 'default'} />
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">{f.irdReference ?? '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => setEditDialog({ ...f })}><Edit fontSize="small" /></IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={() => deleteFiling(f.id)}><Delete fontSize="small" /></IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {filings.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={13} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  No filings for {selectedYear}. Click "Generate IR56B" to auto-create from payroll data.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Summary note */}
      {filings.length > 0 && (
        <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#f8f9fa', borderRadius: 1, borderLeft: '4px solid #1565c0' }}>
          <Typography variant="caption" color="text.secondary">
            <b>IRD Filing Deadline:</b> IR56B is due by <b>1 May</b> each year (for the preceding year of assessment ending 31 March). 
            Ensure all records are in "Ready" status before submitting to eTAX. Export CSV for upload or reference.
          </Typography>
        </Box>
      )}

      {/* ── GENERATE DIALOG ── */}
      <Dialog open={generateDialog} onClose={() => setGenerateDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Generate IR56B Filings
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setGenerateDialog(false)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            This will auto-create one IR56B per active employee for <b>{selectedYear}</b>, pulling salary and MPF data from payroll records. Existing filings will be skipped.
          </Alert>
          <Typography variant="body2">Year of Assessment: <b>{selectedYear}</b></Typography>
          <Typography variant="body2">Period: <b>1 Apr {selectedYear?.split('/')[0]} – 31 Mar 20{selectedYear?.split('/')[1]}</b></Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateDialog(false)}>Cancel</Button>
          <Button variant="contained" sx={{ bgcolor: '#c62828' }} onClick={handleGenerate} startIcon={<Autorenew />}>
            Generate
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── SUBMIT DIALOG ── */}
      <Dialog open={!!submitDialog} onClose={() => setSubmitDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Mark as Submitted
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setSubmitDialog(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ mb: 2 }}>Marking {submitDialog?.ids.length} filing{submitDialog?.ids.length !== 1 ? 's' : ''} as submitted to IRD.</Typography>
          <TextField fullWidth label="IRD eTAX Reference (optional)" value={submitDialog?.ref ?? ''}
            onChange={e => setSubmitDialog(p => p ? { ...p, ref: e.target.value } : null)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubmitDialog(null)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleBulkSubmit} startIcon={<Send />}>Confirm</Button>
        </DialogActions>
      </Dialog>

      {/* ── EDIT DIALOG ── */}
      <Dialog open={!!editDialog} onClose={() => setEditDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editDialog?.id ? 'Edit Filing' : 'New Filing'} — {editDialog?.employee ? `${editDialog.employee.lastName} ${editDialog.employee.firstName}` : ''}
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setEditDialog(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="HKID / Passport" fullWidth value={editDialog?.hkidOrPassport ?? ''}
                onChange={e => setEditDialog(p => ({ ...p, hkidOrPassport: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="Position" fullWidth value={editDialog?.position ?? ''}
                onChange={e => setEditDialog(p => ({ ...p, position: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField select label="Status" fullWidth value={editDialog?.status ?? 'draft'}
                onChange={e => setEditDialog(p => ({ ...p, status: e.target.value }))}>
                {['draft','ready','submitted'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Remuneration (HKD)</Typography>
            </Grid>
            {([
              ['basicSalary', 'Basic Salary'],
              ['bonus', 'Bonus'],
              ['commission', 'Commission'],
              ['allowances', 'Allowances'],
              ['backpay', 'Back Pay'],
              ['otherIncome', 'Other Income'],
            ] as [string, string][]).map(([field, label]) => (
              <Grid key={field} size={{ xs: 6, sm: 4 }}>
                <TextField label={label} type="number" fullWidth
                  value={(editDialog as any)?.[field] ?? ''}
                  onChange={e => setEditDialog(p => ({ ...p, [field]: Number(e.target.value) }))} />
              </Grid>
            ))}

            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Benefits in Kind & MPF</Typography>
            </Grid>
            {([
              ['housingBenefit', 'Housing Benefit'],
              ['otherBenefits', 'Other Benefits'],
              ['mpfEmployeeContrib', 'MPF (Employee)'],
              ['mpfEmployerContrib', 'MPF (Employer)'],
            ] as [string, string][]).map(([field, label]) => (
              <Grid key={field} size={{ xs: 6, sm: 3 }}>
                <TextField label={label} type="number" fullWidth
                  value={(editDialog as any)?.[field] ?? ''}
                  onChange={e => setEditDialog(p => ({ ...p, [field]: Number(e.target.value) }))} />
              </Grid>
            ))}

            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="IRD Reference" fullWidth value={editDialog?.irdReference ?? ''}
                onChange={e => setEditDialog(p => ({ ...p, irdReference: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField label="Notes" fullWidth value={editDialog?.notes ?? ''}
                onChange={e => setEditDialog(p => ({ ...p, notes: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(null)}>Cancel</Button>
          <Button variant="contained" sx={{ bgcolor: '#c62828' }} onClick={saveEdit}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
